/**
 * RecordPaymentDialog — Dialog form for recording a borrower payment.
 *
 * Pre-populates from the scheduled payment row and lets the user
 * enter actual amounts received, payment method, and reference info.
 */

import { useState, useEffect, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { toLocalDateString } from '@/utils/dateHelpers';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { createComponentLogger } from '@/utils/logger';
import {
  toNumber,
  type ServicingPayment,
  type RecordPaymentInput
} from '@/types/servicing';

const log = createComponentLogger('RecordPaymentDialog', 'servicing');

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RecordPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  servicingLoanId: string;
  nextPaymentNumber: number;
  payments: ServicingPayment[];
  onSuccess: () => Promise<void>;
  handleRecordPayment: (input: RecordPaymentInput) => Promise<{ success: boolean; error?: string }>;
  userId?: string;
}

// ---------------------------------------------------------------------------
// Payment methods
// ---------------------------------------------------------------------------

const PAYMENT_METHODS = [
  { value: 'check', label: 'Check' },
  { value: 'ach', label: 'ACH' },
  { value: 'wire', label: 'Wire' }
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RecordPaymentDialog({
  open,
  onOpenChange,
  servicingLoanId,
  nextPaymentNumber,
  payments,
  onSuccess,
  handleRecordPayment,
  userId
}: RecordPaymentDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [paymentNumber, setPaymentNumber] = useState<number>(nextPaymentNumber);
  const [dateReceived, setDateReceived] = useState<string>(
    toLocalDateString()
  );
  const [principal, setPrincipal] = useState<string>('');
  const [interest, setInterest] = useState<string>('');
  const [escrow, setEscrow] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('check');
  const [referenceNumber, setReferenceNumber] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Find the matching scheduled payment for auto-populate
  const scheduledPayment = useMemo(
    () => payments.find((p) => p.payment_number === paymentNumber),
    [payments, paymentNumber]
  );
  const isReplacementPayment = scheduledPayment?.status === 'bounced';

  // Reset form when dialog opens or nextPaymentNumber changes
  useEffect(() => {
    if (open) {
      setPaymentNumber(nextPaymentNumber);
      setDateReceived(toLocalDateString());
      setPaymentMethod('check');
      setReferenceNumber('');
      setNotes('');
      setSubmitting(false);
    }
  }, [open, nextPaymentNumber]);

  // Auto-populate amounts when payment number changes
  useEffect(() => {
    if (scheduledPayment) {
      const p = toNumber(scheduledPayment.principal_due);
      const i = toNumber(scheduledPayment.interest_due);
      const e = toNumber(scheduledPayment.escrow_due);

      setPrincipal(p > 0 ? p.toFixed(2) : '');
      setInterest(i > 0 ? i.toFixed(2) : '');
      setEscrow(e > 0 ? e.toFixed(2) : '');
    } else {
      setPrincipal('');
      setInterest('');
      setEscrow('');
    }
  }, [scheduledPayment]);

  // Computed total from schedule-locked P/I/E amounts
  const totalAmount = useMemo(() => {
    const p = toNumber(principal);
    const i = toNumber(interest);
    const e = toNumber(escrow);
    return (p + i + e).toFixed(2);
  }, [principal, interest, escrow]);

  // -----------------------------------------------------------------------
  // Submit
  // -----------------------------------------------------------------------

  const handleSubmit = async () => {
    // Validation
    if (!dateReceived) {
      toast.error('Missing date', { description: 'Please enter the date the payment was received.' });
      return;
    }
    if (toNumber(totalAmount) <= 0) {
      toast.error('No payment scheduled', { description: 'No scheduled amount for this payment.' });
      return;
    }

    setSubmitting(true);
    log.info('Recording payment', {
      servicingLoanId,
      paymentNumber,
      totalAmount: toNumber(totalAmount)
    });

    const input: RecordPaymentInput = {
      servicing_loan_id: servicingLoanId,
      payment_number: paymentNumber,
      payment_received_date: dateReceived,
      principal_received: toNumber(principal),
      interest_received: toNumber(interest),
      escrow_received: toNumber(escrow),
      total_amount_received: toNumber(totalAmount),
      payment_method: paymentMethod || undefined,
      reference_number: referenceNumber || undefined,
      notes: notes || undefined,
      recorded_by: userId
    };

    try {
      const result = await handleRecordPayment(input);
      if (result.success) {
        toast.success('Payment recorded', { description: `Payment #${paymentNumber} has been recorded successfully.` });
        log.info('Payment recorded successfully', { paymentNumber });
        await onSuccess();
      } else {
        toast.error('Error recording payment', { description: result.error || 'An unexpected error occurred.' });
        log.error('Payment recording failed', { paymentNumber, error: result.error });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Error', { description: msg });
      log.error('Payment recording exception', { paymentNumber }, err);
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            {isReplacementPayment
              ? 'Enter the replacement payment received after the original payment bounced or was reversed.'
              : 'Enter the payment details received from the borrower.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Payment Number */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="payment-number">Payment #</Label>
              <Input
                id="payment-number"
                type="number"
                min={1}
                value={paymentNumber}
                readOnly
                className="bg-muted"
              />
            </div>

            {/* Date Received */}
            <div className="space-y-2">
              <Label htmlFor="date-received">Date Received</Label>
              <Input
                id="date-received"
                type="date"
                value={dateReceived}
                onChange={(e) => setDateReceived(e.target.value)}
              />
            </div>
          </div>

          {/* Total Amount (computed) */}
          <div className="space-y-2">
            <Label htmlFor="total-amount">Total Amount Received</Label>
            <Input
              id="total-amount"
              type="number"
              step="0.01"
              min="0"
              value={totalAmount}
              readOnly
              className="bg-muted font-medium"
            />
          </div>

          {/* Principal / Interest / Escrow */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="principal">Principal</Label>
              <Input
                id="principal"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={principal}
                readOnly
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="interest">Interest</Label>
              <Input
                id="interest"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={interest}
                readOnly
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="escrow">Escrow</Label>
              <Input
                id="escrow"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={escrow}
                readOnly
                className="bg-muted"
              />
            </div>
          </div>

          {/* Payment Method / Reference */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="payment-method">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger id="payment-method">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reference-number">Reference Number</Label>
              <Input
                id="reference-number"
                type="text"
                placeholder="Check # or transaction ID"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Optional notes about this payment..."
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Recording...
              </>
            ) : (
              'Record Payment'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
